import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { dt } from "../../../shared/themes/redesignTokens";

// ════════════════════════════════════════════════════════════════
//  DeadlineBar — horizontal progress bar showing time remaining
// ════════════════════════════════════════════════════════════════

interface DeadlineBarProps {
  /** ISO deadline timestamp */
  deadline: string;
  /** ISO start timestamp (for calculating total window) */
  startDate?: string;
  /** Optional label text override */
  label?: string;
  /** Compact mode (smaller height) */
  compact?: boolean;
}

const PULSE_STYLE_ID = "deadline-bar-pulse-keyframes";

function ensurePulseKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(PULSE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes deadlinePulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);
}

/** Returns progress color based on percentage of time remaining */
function getProgressColor(percentRemaining: number, isOverdue: boolean): string {
  if (isOverdue) return "#EF4444";
  if (percentRemaining > 50) return "#27AE60";
  if (percentRemaining > 25) return "#F59E0B";
  return "#EF4444";
}

/** Formats remaining or overdue time as a human-readable string */
function formatTimeRemaining(
  diffMs: number,
  translate: (key: string, options?: any) => any
): string {
  const absDiff = Math.abs(diffMs);
  const totalSeconds = Math.floor(absDiff / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  let timeStr: string;
  if (totalDays > 0) {
    const remainingHours = totalHours - totalDays * 24;
    timeStr = translate("serviceOffers:deadlineBar.days", { count: totalDays });
    if (remainingHours > 0) {
      timeStr += " " + translate("serviceOffers:deadlineBar.hours", { count: remainingHours });
    }
  } else if (totalHours > 0) {
    const remainingMinutes = totalMinutes - totalHours * 60;
    timeStr = translate("serviceOffers:deadlineBar.hours", { count: totalHours });
    if (remainingMinutes > 0) {
      timeStr += " " + translate("serviceOffers:deadlineBar.minutes", { count: remainingMinutes });
    }
  } else if (totalMinutes > 0) {
    timeStr = translate("serviceOffers:deadlineBar.minutes", { count: totalMinutes });
  } else {
    timeStr = translate("serviceOffers:deadlineBar.seconds", { count: totalSeconds });
  }

  const isOverdue = diffMs < 0;
  return isOverdue
    ? translate("serviceOffers:deadlineBar.overdue", { time: timeStr })
    : translate("serviceOffers:deadlineBar.remaining", { time: timeStr });
}

const DeadlineBar: React.FC<DeadlineBarProps> = ({
  deadline,
  startDate,
  label,
  compact = false,
}) => {
  const { translate } = useTranslation(["serviceOffers"]);
  useRedesignSetup();

  const [now, setNow] = useState(() => dayjs());

  // Ensure pulse keyframes are available
  useEffect(() => {
    ensurePulseKeyframes();
  }, []);

  // Timer: re-render every 60s normally, every 1s if <5 min remaining
  useEffect(() => {
    const deadlineMs = dayjs(deadline).valueOf();

    const getInterval = () => {
      const remaining = deadlineMs - Date.now();
      return remaining > 0 && remaining < 5 * 60 * 1000 ? 1000 : 60000;
    };

    let intervalId = setInterval(() => {
      setNow(dayjs());
    }, getInterval());

    // Re-check interval every 30s to switch between fast/slow
    const checkId = setInterval(() => {
      clearInterval(intervalId);
      intervalId = setInterval(() => {
        setNow(dayjs());
      }, getInterval());
    }, 30000);

    return () => {
      clearInterval(intervalId);
      clearInterval(checkId);
    };
  }, [deadline]);

  const deadlineMoment = dayjs(deadline);
  const startMoment = startDate ? dayjs(startDate) : deadlineMoment.subtract(24, "hour");

  const totalWindowMs = deadlineMoment.diff(startMoment);
  const remainingMs = deadlineMoment.diff(now);
  const isOverdue = remainingMs < 0;

  // Calculate percentage of time remaining (0-100)
  let percentRemaining = 100;
  if (totalWindowMs > 0) {
    percentRemaining = Math.max(0, Math.min(100, (remainingMs / totalWindowMs) * 100));
  }
  if (isOverdue) {
    percentRemaining = 0;
  }

  const barColor = getProgressColor(percentRemaining, isOverdue);
  const barHeight = compact ? 4 : 6;
  const barRadius = compact ? 2 : 3;
  const timeText = label || formatTimeRemaining(remainingMs, translate);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Progress bar */}
      <Box
        sx={{
          width: "100%",
          height: `${barHeight}px`,
          borderRadius: `${barRadius}px`,
          backgroundColor: "#E5E7EB",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${isOverdue ? 100 : 100 - percentRemaining}%`,
            height: "100%",
            borderRadius: `${barRadius}px`,
            backgroundColor: barColor,
            transition: "width 1s ease, background-color 0.5s ease",
            ...(isOverdue && {
              animation: "deadlinePulse 1.5s ease-in-out infinite",
              width: "100%",
            }),
          }}
        />
      </Box>
      {/* Time remaining text */}
      <Box
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "10px",
          color: isOverdue ? "#EF4444" : dt.textMuted,
          fontWeight: isOverdue ? 600 : 400,
          mt: "3px",
          lineHeight: 1.2,
        }}
      >
        {timeText}
      </Box>
    </Box>
  );
};

export default DeadlineBar;
